import { AxiosError } from 'axios';

import { useEffect, useMemo, useState } from 'react';

import {
    ActionIcon,
    Button,
    Checkbox,
    Group,
    Modal,
    PasswordInput,
    Select,
    Text,
    TextInput,
    Tooltip
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

import {
    AzureDevOpsProjectDto,
    AzureDevOpsRepoDto,
    FirefinchProjectDto,
    ProjectDto,
    ProjectUploadDto
} from '../../api/models';
import { useGetApiNotionProject } from '../../api/notion/notion.ts';
import { customInstance } from '../../../orvalCustomAxiosInstance';
import ServerLoadErrorFallback from '../utils/ServerLoadErrorFallback.tsx';

interface ProjectModalProps {
    opened: boolean;
    onClose: () => void;
    onSubmit: (data: ProjectUploadDto) => Promise<void>;
    error: AxiosError | null;
    isPending: boolean;
    refetch: () => Promise<ProjectDto[]>;
    project?: ProjectDto;
    title: string;
    existingProjects?: ProjectDto[];
}

const HelpIcon = ({ tooltip }: { tooltip: string }) => (
    <Tooltip label={tooltip} multiline w={300} withArrow>
        <ActionIcon variant="subtle" size="xs" color="gray" mb={7}>
            <IconInfoCircle size={16} />
        </ActionIcon>
    </Tooltip>
);

const ProjectModal = ({
    opened,
    onClose,
    onSubmit,
    error,
    isPending,
    refetch,
    project,
    title,
    existingProjects
}: ProjectModalProps) => {
    const [selectedProject, setSelectedProject] =
        useState<FirefinchProjectDto>();
    const { data: allProjects, isLoading: loadingProjects } =
        useGetApiNotionProject();

    // DevOps fields
    const [includeRepo, setIncludeRepo] = useState(false);
    const [devOpsOrgUrl, setDevOpsOrgUrl] = useState('');
    const [devOpsPat, setDevOpsPat] = useState('');
    const [devOpsProjectName, setDevOpsProjectName] = useState('');
    const [selectedRepoId, setSelectedRepoId] = useState<string | null>(null);
    const [devOpsProjects, setDevOpsProjects] = useState<AzureDevOpsProjectDto[]>([]);
    const [loadingDevOpsProjects, setLoadingDevOpsProjects] = useState(false);
    const [repos, setRepos] = useState<AzureDevOpsRepoDto[]>([]);
    const [loadingRepos, setLoadingRepos] = useState(false);

    // Check if selected project already exists in the database
    const projectAlreadyExists = useMemo(() => {
        if (!selectedProject || !existingProjects) return false;
        return existingProjects.some(
            existingProject => existingProject.id === selectedProject.id
        );
    }, [selectedProject, existingProjects]);

    // Pre-select project when editing
    useEffect(() => {
        if (opened && allProjects && project) {
            const proj = allProjects.find(
                (p: FirefinchProjectDto) => p.id === project.id
            );
            setSelectedProject(proj);
        }
    }, [opened, allProjects, project]);

    // Pre-fill DevOps fields when editing
    useEffect(() => {
        if (opened && project) {
            const hasDevOps = !!project.devOpsRepoId;
            setIncludeRepo(hasDevOps);
            setDevOpsOrgUrl(project.devOpsOrgUrl ?? '');
            setDevOpsPat('');
            setDevOpsProjectName(project.devOpsProjectName ?? '');
            setSelectedRepoId(project.devOpsRepoId ?? null);
        }
    }, [opened, project]);

    useEffect(() => {
        if (!opened) {
            setSelectedProject(undefined);
            setIncludeRepo(false);
            setDevOpsOrgUrl('');
            setDevOpsPat('');
            setDevOpsProjectName('');
            setSelectedRepoId(null);
            setDevOpsProjects([]);
            setRepos([]);
        }
    }, [opened]);

    // PAT is available either from user input or stored on the server
    const hasPat = !!devOpsPat || !!project?.hasDevOpsPat;

    // A client with no name can't be created server-side (Clients.Name is required); this is usually
    // a blank client name in Notion. Block submission and explain rather than letting it 500.
    const clientNameMissing = !!selectedProject && !selectedProject.clientName?.trim();

    const devOpsValid = !includeRepo || (devOpsOrgUrl && devOpsProjectName && selectedRepoId && hasPat);
    const canSubmit =
        selectedProject && !projectAlreadyExists && !clientNameMissing && devOpsValid;

    // Fetch DevOps projects when org URL is filled and a PAT is available
    const canFetchDevOpsProjects = includeRepo && devOpsOrgUrl && hasPat;

    useEffect(() => {
        if (!canFetchDevOpsProjects) {
            setDevOpsProjects([]);
            return;
        }

        let cancelled = false;
        setLoadingDevOpsProjects(true);

        customInstance<AzureDevOpsProjectDto[]>({
            url: '/api/AzureDevOps/projects',
            method: 'POST',
            data: {
                orgUrl: devOpsOrgUrl,
                ...(devOpsPat ? { pat: devOpsPat } : {}),
                ...(project?.id ? { projectId: project.id } : {})
            }
        })
            .then(data => {
                if (!cancelled) setDevOpsProjects(data);
            })
            .catch(err => {
                console.error('Failed to fetch DevOps projects:', err);
                if (!cancelled) setDevOpsProjects([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingDevOpsProjects(false);
            });

        return () => { cancelled = true; };
    }, [canFetchDevOpsProjects, devOpsOrgUrl, devOpsPat, project?.id]);

    // Fetch repos when all DevOps fields are filled and a PAT is available
    const canFetchRepos = includeRepo && devOpsOrgUrl && devOpsProjectName && hasPat;

    useEffect(() => {
        if (!canFetchRepos) {
            setRepos([]);
            return;
        }

        let cancelled = false;
        setLoadingRepos(true);

        customInstance<AzureDevOpsRepoDto[]>({
            url: '/api/AzureDevOps/repos',
            method: 'POST',
            data: {
                orgUrl: devOpsOrgUrl,
                projectName: devOpsProjectName,
                ...(devOpsPat ? { pat: devOpsPat } : {}),
                ...(project?.id ? { projectId: project.id } : {})
            }
        })
            .then(data => {
                if (!cancelled) setRepos(data);
            })
            .catch(() => {
                if (!cancelled) setRepos([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingRepos(false);
            });

        return () => { cancelled = true; };
    }, [canFetchRepos, devOpsOrgUrl, devOpsPat, devOpsProjectName, project?.id]);

    const handleSubmit = async () => {
        if (!canSubmit) return;

        const data: ProjectUploadDto = {
            id: selectedProject.id,
            name: selectedProject.name,
            code: selectedProject.code,
            isMedical: false,
            client: {
                id: selectedProject.clientId ?? '',
                name: selectedProject.clientName
            },
            devOpsOrgUrl: includeRepo ? devOpsOrgUrl || undefined : undefined,
            devOpsPat: includeRepo && devOpsPat ? devOpsPat : undefined,
            devOpsProjectName: includeRepo ? devOpsProjectName || undefined : undefined,
            devOpsRepoId: includeRepo ? selectedRepoId ?? undefined : undefined
        };

        try {
            await onSubmit(data);
            await refetch();
            onClose();
        } catch (err) {
            console.error('Error submitting the project:', err);
        }
    };

    // Group projects by client name
    const groupedData = useMemo(() => {
        if (!allProjects) return [];
        const groups = new Map<string, { value: string; label: string }[]>();
        for (const p of allProjects) {
            const groupName = p.clientName ?? 'Unknown Client';
            if (!groups.has(groupName)) {
                groups.set(groupName, []);
            }
            groups.get(groupName)!.push({
                value: p.id,
                label: p.name ?? 'Unnamed Project'
            });
        }
        return Array.from(groups.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([group, items]) => ({ group, items }));
    }, [allProjects]);

    return (
        <Modal opened={opened} onClose={onClose} title={title} size="md" closeOnClickOutside={false}>
            {error && <ServerLoadErrorFallback entity={title} error={error} />}
            <Select
                label='Project'
                mt='sm'
                searchable
                disabled={loadingProjects}
                placeholder={
                    loadingProjects
                        ? 'Loading projects...'
                        : 'Select a project'
                }
                data={groupedData}
                value={selectedProject?.id ?? null}
                onChange={value => {
                    setSelectedProject(
                        allProjects?.find(
                            (p: FirefinchProjectDto) => p.id === value
                        )
                    );
                }}
            />

            {projectAlreadyExists && (
                <Text c='orange' size='sm' mt='md'>
                    This project already exists in the database. Please select a
                    different project.
                </Text>
            )}

            {clientNameMissing && !projectAlreadyExists && (
                <Text c='orange' size='sm' mt='md'>
                    This project&apos;s client has no name in Notion. Set the
                    client name in Notion, then try again.
                </Text>
            )}

            <Group mt="lg" gap="xs" align="center">
                <Checkbox
                    label="Include repository documents"
                    checked={includeRepo}
                    onChange={e => setIncludeRepo(e.currentTarget.checked)}
                />
                <HelpIcon tooltip="Enable this to browse and edit markdown files from an Azure DevOps Git repository. Edits will create pull requests in the linked repository." />
            </Group>

            {includeRepo && (
                <>
                    <Group mt="sm" gap="xs" align="flex-end">
                        <TextInput
                            label="Organization URL"
                            placeholder="https://dev.azure.com/myorg"
                            value={devOpsOrgUrl}
                            onChange={e => {
                                setDevOpsOrgUrl(e.currentTarget.value);
                                setDevOpsProjectName('');
                                setSelectedRepoId(null);
                            }}
                            style={{ flex: 1 }}
                        />
                        <HelpIcon tooltip="The Azure DevOps organization URL, e.g. https://dev.azure.com/myorg" />
                    </Group>

                    <Group mt="sm" gap="xs" align="flex-end">
                        <PasswordInput
                            label="Personal Access Token"
                            placeholder={
                                project?.hasDevOpsPat
                                    ? 'PAT configured \u2014 enter new value to change'
                                    : 'Enter PAT'
                            }
                            value={devOpsPat}
                            onChange={e => {
                                setDevOpsPat(e.currentTarget.value);
                                setDevOpsProjectName('');
                                setSelectedRepoId(null);
                            }}
                            style={{ flex: 1 }}
                        />
                        <HelpIcon tooltip="A Personal Access Token with Code (Read) scope. Write access is not required. The token is encrypted at rest and never displayed after saving." />
                    </Group>

                    <Group mt="sm" gap="xs" align="flex-end">
                        <Select
                            label="DevOps Project"
                            searchable
                            placeholder={
                                loadingDevOpsProjects
                                    ? 'Loading...'
                                    : !canFetchDevOpsProjects
                                      ? 'Fill in credentials above to load projects'
                                      : 'Select a project'
                            }
                            disabled={!canFetchDevOpsProjects && !devOpsProjectName}
                            data={devOpsProjects.map(p => ({
                                value: p.name,
                                label: p.name
                            }))}
                            value={devOpsProjectName || null}
                            onChange={value => {
                                setDevOpsProjectName(value ?? '');
                                setSelectedRepoId(null);
                            }}
                            style={{ flex: 1 }}
                        />
                        <HelpIcon tooltip="The project within Azure DevOps that contains the repository." />
                    </Group>

                    <Group mt="sm" gap="xs" align="flex-end">
                        <Select
                            label="Repository"
                            searchable
                            placeholder={
                                loadingRepos
                                    ? 'Loading...'
                                    : !canFetchRepos
                                      ? 'Select a DevOps project above to load repositories'
                                      : 'Select a repository'
                            }
                            disabled={!canFetchRepos && !selectedRepoId}
                            data={repos.map((r: AzureDevOpsRepoDto) => ({
                                value: r.id,
                                label: r.name
                            }))}
                            value={selectedRepoId}
                            onChange={setSelectedRepoId}
                            style={{ flex: 1 }}
                        />
                        <HelpIcon tooltip="The Git repository to link. Repositories are loaded using the credentials above." />
                    </Group>
                </>
            )}

            <Group mt='lg' justify='flex-end'>
                <Button onClick={onClose} variant='outline'>
                    Cancel
                </Button>
                <Button
                    variant='filled'
                    onClick={handleSubmit}
                    loading={isPending}
                    disabled={!canSubmit}
                >
                    Submit
                </Button>
            </Group>
        </Modal>
    );
};

export default ProjectModal;
